import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TRACK_OPTIONS } from "@/lib/school";
import { monthRange, monthOf } from "@/lib/vigilanza";
import { StatsKpis } from "@/components/mansioni/stats/StatsKpis";
import { StatsLoadChart } from "@/components/mansioni/stats/StatsLoadChart";
import { StatsMatrix } from "@/components/mansioni/stats/StatsMatrix";

const sel = "mt-1.5 h-9 rounded-md border border-stone-200 bg-white px-2 text-sm block";
const PRESETS = [["Questo mese", () => monthRange(0)], ["Mese scorso", () => monthRange(-1)],
  ["Ultimi 3 mesi", () => ({ from: monthRange(-2).from, to: monthRange(0).to })],
  ["Anno accademico", () => { const t = monthRange(0).from; const y = Number(t.slice(0, 4)) - (Number(t.slice(5, 7)) < 9 ? 1 : 0); return { from: `${y}-09-01`, to: monthOf(`${y + 1}-08-01`).to }; }]];

export default function StatisticheTurni() {
  const [range, setRange] = useState(monthRange(0));
  const [f, setF] = useState({ year_level: "", track_type: "", gender: "" });
  const [view, setView] = useState("chart");
  const { data, isLoading } = useQuery({
    queryKey: ["turni-stats", range, f], enabled: !!range.from && !!range.to && range.from <= range.to,
    queryFn: async () => (await api.get("/mansioni-area/stats", { params: { since: range.from, until: range.to, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) } })).data,
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1500px] mx-auto" data-testid="stats-turni-page">
      <PageHeader title="Statistiche turni" subtitle="Distribuzione dei carichi delle mansioni per studente: chi ne fa di più, chi di meno e su quali mansioni."/>
      <Card className="p-4 border border-stone-200 shadow-none rounded-xl flex flex-wrap items-end gap-4 mb-6">
        <div className="flex gap-1.5 flex-wrap">
          {PRESETS.map(([l, fn]) => (
            <button key={l} onClick={() => setRange(fn())} data-testid={`stats-preset-${l.replace(/\s/g, "-").toLowerCase()}`}
              className="px-3 py-1.5 rounded-full text-xs border border-stone-200 bg-white hover:border-stone-400 transition-colors">{l}</button>
          ))}
        </div>
        <div><Label className="text-xs">Dal</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1.5 w-40" data-testid="stats-from"/></div>
        <div><Label className="text-xs">Al</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1.5 w-40" data-testid="stats-to"/></div>
        <div><Label className="text-xs">Anno</Label>
          <select value={f.year_level} onChange={(e) => setF({ ...f, year_level: e.target.value })} className={sel} data-testid="stats-year">
            <option value="">Tutti</option>{[1, 2, 3].map((y) => <option key={y} value={y}>{y}° anno</option>)}
          </select></div>
        <div><Label className="text-xs">Percorso</Label>
          <select value={f.track_type} onChange={(e) => setF({ ...f, track_type: e.target.value })} className={`${sel} capitalize`} data-testid="stats-track">
            <option value="">Tutti</option>{TRACK_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><Label className="text-xs">Sesso</Label>
          <select value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })} className={sel} data-testid="stats-gender">
            <option value="">Tutti</option><option value="M">Uomini</option><option value="F">Donne</option>
          </select></div>
      </Card>

      {isLoading && <div className="text-sm text-stone-400">Calcolo…</div>}
      {data && (
        <>
          <StatsKpis s={data.summary}/>
          <div className="flex items-center justify-between mt-8 mb-3">
            <h2 className="text-base md:text-lg font-semibold text-[#1C352D]">Carico per studente</h2>
            <div className="inline-flex rounded-full border border-stone-200 p-0.5 bg-white">
              {[["chart", "Grafico"], ["matrix", "Tabella per mansione"]].map(([k, l]) => (
                <button key={k} onClick={() => setView(k)} data-testid={`stats-view-${k}`} className={`px-3 py-1 text-xs rounded-full transition-colors ${view === k ? "bg-[#1C352D] text-white" : "text-stone-600"}`}>{l}</button>
              ))}
            </div>
          </div>
          {data.students.length === 0
            ? <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessuno studente attivo con questi filtri.</Card>
            : view === "chart" ? <StatsLoadChart data={data}/> : <StatsMatrix data={data}/>}
        </>
      )}
    </div>
  );
}
